<template>
  <tippy
    ref="tippyRef"
    class="w-full"
    :interactive="true"
    :arrow="false"
    trigger="click"
    placement="auto"
    :duration="100"
    theme="daytimepicker"
    :append-to="() => appendTarget"
  >
    <template #default="{ state }">
      <div class="w-full relative">
        <div
          class="flex rounded border items-center cursor-pointer"
          :class="[
            'border-n-100 dark:border-n-600',
            ...sizeClasses,
            ...backgroundColorsClasses,
            ...focusClasses,
            ...textColorsClasses
          ]"
        >
          <div
            v-if="showArrows"
            @click.stop="goPrevious()"
            class="px-2 border-r border-n-100 dark:border-n-600 h-full flex items-center shrink-0 select-none"
          >
            <l-icon name="bracket-left" class="text-v-500 dark:text-v-300" />
          </div>
          <div
            class="flex-1 flex items-center justify-between gap-2 shrink min-w-0"
            :class="[...paddingClasses]"
          >
            <l-icon v-if="showIcon" name="calendar" class="text-n-200 dark:text-n-0" />
            <div class="flex-1 whitespace-nowrap select-none text-ellipsis overflow-hidden">
              <slot v-bind="{ date }">
                <template v-if="actualDate">
                  {{ showTime ? dateText : dayText }}
                </template>
                <div v-else class="ellipsis text-n-200">
                  {{ placeholder }}
                </div>
              </slot>
            </div>
            <l-icon
              name="bracket-down"
              class="transition-all duration-300 text-v-500 dark:text-n-0 select-none"
              :class="{
                'rotate-180': state.isVisible
              }"
            />
          </div>
          <div
            v-if="showArrows"
            @click.stop="goNext"
            class="px-2 border-l border-n-100 dark:border-n-600 h-full flex items-center shrink-0 select-none"
            :class="{
              'pointer-events-none opacity-50': isNextDisabled
            }"
          >
            <l-icon
              name="bracket-right"
              class="text-v-500 dark:text-v-300"
              :class="{
                '!text-n-300 dark:!text-n-500': isNextDisabled
              }"
            />
          </div>
        </div>
      </div>
    </template>
    <template #content>
      <div
        class="w-72 border border-n-100 dark:border-n-900 rounded-lg px-4 py-6 flex flex-col gap-6 bg-n-0 dark:bg-n-800 z-10"
      >
        <l-calendar
          v-model="localModel"
          @update:model-value="!showTime && save()"
          :only-future="onlyFuture"
          :only-past="onlyPast"
          :default-time="defaultTime"
          :unavailable-dates="unavailableDates"
          :timezone="timezone"
          @month-changed="month => $emit('monthChanged', month)"
        />
        <l-time-picker v-if="showTime" v-model.date="localModel" />
        <l-button v-if="showTime" class="w-full" size="small" @click="save">
          {{ t('ctas.apply') }}
        </l-button>
      </div>
    </template>
  </tippy>
</template>

<script setup lang="ts">
import { addDays, format, isSameDay } from 'date-fns'
import { computed, ref, useTemplateRef, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { Tippy } from 'vue-tippy'

import {
  useBackgroundColorsClasses,
  useFocusClasses,
  usePaddingClasses,
  useSizeClasses,
  useTextColorsClasses
} from '../classes'
import LButton from '../LButton.vue'
import LIcon from '../LIcon.vue'
import type { Size } from '../types'
import LCalendar from './LCalendar.vue'
import LTimePicker from './LTimePicker.vue'

type Props = {
  /** Show time picker behind calendar. We show an apply button to confirm. */
  showTime?: boolean
  /** Show icon in the input */
  showIcon?: boolean
  onlyFuture?: boolean
  onlyPast?: boolean
  placeholder?: string
  size?: Size
  /**
   * Default time of the date
   * If not provided, the current time will be used
   * Format HH:mm
   */
  defaultTime?: string
  /** Dates that can't be selected */
  unavailableDates?: Date[]
  /** Show arrow quick actions */
  showArrows?: boolean
  /** Timezone to use for the dates */
  timezone?: string
}
const { t } = useI18n()

const tippyRef = useTemplateRef<typeof Tippy>('tippyRef')
const appendTarget = document.body

const props = withDefaults(defineProps<Props>(), {
  showTime: true,
  showIcon: true,
  onlyFuture: false,
  onlyPast: false,
  placeholder: '',
  size: 'small',
  defaultTime: undefined,
  unavailableDates: () => [],
  showArrows: false,
  timezone: undefined
})

const model = defineModel<string | null>({
  default: null
})
const date = defineModel<Date | null>('date', {
  default: null
})

defineEmits<{
  /** Emitted when the visible month changes */
  monthChanged: [Date]
}>()

const localModel = ref<Date | null>(null)

watch(
  [model, date],
  value => {
    const currentDate = value[0] ?? value[1]
    localModel.value = typeof currentDate === 'string' ? new Date(currentDate) : currentDate
  },
  {
    immediate: true
  }
)

const actualDate = computed(() => {
  return model.value ? new Date(model.value) : date.value
})

const dateText = computed(() => {
  if (!actualDate.value) return null
  return format(actualDate.value, 'dd/MM/yyyy HH:mm')
})

const dayText = computed(() => {
  if (!actualDate.value) return null
  return format(actualDate.value, 'dd/MM/yyyy')
})

function goNext() {
  if (props.onlyPast && isNextDisabled.value) return

  localModel.value = addDays(localModel.value ?? new Date(), 1)
  save()
}

function goPrevious() {
  if (props.onlyFuture && isSameDay(localModel.value ?? new Date(), new Date())) {
    return
  }
  localModel.value = addDays(localModel.value ?? new Date(), -1)
  save()
}

function save() {
  date.value = localModel.value
  model.value = localModel.value ? localModel.value.toISOString() : null
  tippyRef.value?.hide()
}

const isNextDisabled = computed(() => {
  if (props.onlyPast) {
    const yesterday = addDays(new Date(), -1)
    yesterday.setHours(0, 0, 0, 0)
    const currentDay = localModel.value ?? new Date()
    currentDay.setHours(0, 0, 0, 0)
    return currentDay >= yesterday
  }
  return false
})

const sizeClasses = computed(() => useSizeClasses(props.size))
const paddingClasses = computed(() => usePaddingClasses(props.size))
const backgroundColorsClasses = computed(() => useBackgroundColorsClasses())
const focusClasses = computed(() => useFocusClasses())
const textColorsClasses = computed(() => useTextColorsClasses())
</script>

<style>
@import 'tippy.js/dist/tippy.css';

.tippy-box[data-theme~='daytimepicker'] {
  background: none !important;
}

.tippy-box[data-theme~='daytimepicker'] .tippy-content {
  padding: 0;
}

.tippy-box[data-theme~='daytimepicker'][data-animation='fade'][data-state='hidden'] {
  opacity: 0;
}
</style>
